import {
  Check,
  CircleX,
  FileText,
  FileType,
  Loader2,
  Presentation,
  RefreshCw,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatFileSize } from '@/utils/formatUtils';

import { Button } from '@/components/ui/Button';

import type { AttachedFileStatus } from './useFileAttachments';

interface FileAttachmentCardProps {
  file: File;
  status: AttachedFileStatus;
  error?: string;
  onRemove: () => void;
  onRetry?: () => void;
}

const getFileIcon = (filename: string) => {
  const extension = filename.split('.').pop()?.toLowerCase() ?? '';

  if (['doc', 'docx'].includes(extension)) {
    return FileType;
  }
  if (extension === 'pptx') {
    return Presentation;
  }
  return FileText;
};

const renderStatusIcon = (status: AttachedFileStatus) => {
  if (status === 'parsing' || status === 'uploading') {
    return (
      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
    );
  }
  return null;
};

const renderBottomContent = (
  file: File,
  status: AttachedFileStatus,
  error?: string
) => {
  if (status === 'parsing') {
    return (
      <p className="text-sm text-muted-foreground leading-5 h-[20px] text-left">
        Parsing...
      </p>
    );
  }

  if (status === 'uploading') {
    return (
      <p className="text-sm text-muted-foreground leading-5 h-[20px] text-left">
        Uploading...
      </p>
    );
  }

  if (status === 'error') {
    return (
      <p className="text-sm text-destructive leading-5 h-[20px] text-left truncate">
        {error || 'Parse failed'}
      </p>
    );
  }

  return (
    <p className="text-sm text-muted-foreground leading-5 h-[20px] text-left flex items-center gap-1">
      {formatFileSize(file.size)}
      <Check className="h-3 w-3 text-green-600" />
    </p>
  );
};

const CardActions = ({
  status,
  onRemove,
  onRetry,
}: Pick<FileAttachmentCardProps, 'status' | 'onRemove' | 'onRetry'>) => (
  <div className="flex items-center gap-1">
    {status === 'error' && onRetry && (
      <Button
        variant="ghost"
        size="icon"
        onClick={onRetry}
        className="h-5 w-5 p-0"
        aria-label="Retry parsing"
      >
        <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>
    )}
    <Button
      variant="ghost"
      size="icon"
      onClick={onRemove}
      className="h-5 w-5 p-0"
      aria-label="Remove file"
    >
      <CircleX className="h-4 w-4 text-destructive" />
    </Button>
  </div>
);

export const FileAttachmentCard = ({
  file,
  status,
  error,
  onRemove,
  onRetry,
}: FileAttachmentCardProps) => {
  const Icon = getFileIcon(file.name);

  return (
    <div
      className={cn(
        'border rounded-md p-2 w-[208px] min-w-0 h-[96px] flex-none flex flex-col gap-1 bg-color-transparent items-start overflow-hidden',
        {
          'border-destructive': status === 'error',
          'border-border-subtle': status !== 'error',
        }
      )}
    >
      <div className="flex justify-between h-4 w-full min-w-0 shrink-0">
        {renderStatusIcon(status) || (
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <CardActions status={status} onRemove={onRemove} onRetry={onRetry} />
      </div>
      <div className="flex min-w-0 w-full items-center">
        <p className="w-full min-w-0 text-sm font-semibold text-foreground truncate text-left">
          {file.name}
        </p>
      </div>
      <div className="flex flex-col">
        {renderBottomContent(file, status, error)}
      </div>
    </div>
  );
};
